/*
 * Actions for the [users store]{link module:store/users}
 *
 * @module store/users/actions
 */

import * as apiAmperixes from '@/api/amperixes'
import * as apiEmss from '@/api/energy_management_systems'
import * as apiGroups from '@/api/groups'
import * as apiUsers from '@/api/users'
import { User, Permission } from '@/store/modules/users'

// helper
function mapPermission(permission) {
  return new Permission(permission)
}

/**
 * Action to reset the users
 *
 * @function
 *
 */
export function resetStore({ commit }) {
  commit('RESET_USERS')
  return true
}

/**
 * API call to GET all or filtered (by ID) Users.
 *
 * @function
 *
 * @param {object} params
 * @param {array} params.userIds (optional) is an optional filter. A list of User's IDs. Will request all if empty.
 * @param {string} params.ctxt is the context of the API request.
 * @param {array} params.fields is a list of fields (attributes) to be included in the response
 *
 * @return {promise} which resolves to the list of users (json:api data-response)
 */
export async function getUsers({ commit }, { userIds = [], ctxt = 'admin', fields = ['email', 'roles'] } = {}) {
  const filter = {}
  if (userIds && userIds.length > 0) {
    filter.id = userIds.join(',')
  }
  const rsp = await apiUsers.getUsers({
    ctxt,
    fields,
    filter
  })
  const users = rsp.payload.data

  users.forEach((u) => {
    const certifiedPartnerIdentificationId = u?.relationships?.certifiedPartnerIdentification?.data?.id
    commit(
      'SET_USER',
      new User({
        id: u.id,
        email: u.attributes.email,
        roles: u.attributes.roles,
        certifiedPartnerIdentification: certifiedPartnerIdentificationId
          ? { id: certifiedPartnerIdentificationId }
          : undefined
      })
    )
  })

  return users
}

/**
 * API call to GET one User by ID.
 *
 * @function
 *
 * @param {object} params
 * @param {array} params.userId (required) is the user ID.
 * @param {string} params.ctxt is the context of the API request.
 * @param {array} params.fields is a list of fields (attributes) to be included in the response
 *
 * @return {promise} resolves to the user (json:api data-response)
 */
export async function getUser({ commit }, { userId, ctxt = 'admin', fields = ['email', 'roles'] }) {
  const rsp = await apiUsers.getUser({ id: userId, ctxt, fields })
  const user = rsp.payload.data

  commit(
    'SET_USER',
    new User({
      id: user.id,
      email: user.attributes.email,
      roles: user.attributes.roles
    })
  )

  return user
}

/**
 * Action to get the All Users related to an emsGroup with their permissions per context.
 *
 * Data is currently mocked above. Will be requested through an AJAX request.
 *
 * @function
 *
 * @params {Integer} groupId, the desired groupId
 *
 * @return {object} the users
 */
export async function getUsersForGroup({ rootState, commit }, { groupId, ctxt = 'admin', removeUnmentioned = true }) {
  const rsp = await apiGroups.getUsersForGroup({ id: groupId, ctxt })

  const users = rsp.payload.data

  users.forEach((u) => {
    commit(
      'SET_USER',
      new User({
        id: u.id,
        email: u.attributes.email,
        roles: u.attributes.roles
      })
    )
  })

  const groupUsers = {}

  for (const user of users) {
    const perm = mapPermission(user.attributes.permission)
    if (rootState.users.users[user.id]) {
      for (const role of rootState.users.users[user.id].roles) {
        if (!perm[role]) {
          perm[role] = 0
        }
      }
      groupUsers[user.id] = perm
    }
  }

  commit('SET_USERS_FOR_GROUP', { groupId, users: groupUsers, removeUnmentioned })

  return groupUsers
}

/**
 * Action to get
 * 1. the users directly related to an EMS with their permissions per context.
 * 2. the EMS's `EmsAccessGroup` ID
 *
 *
 * @function
 *
 * @params {Integer} emsId, the desired EMS ID
 *
 * @return {promise} which resolves the the list of user IDs
 */
export async function getUsersForEmsDirect({ commit }, { emsId, ctxt = 'admin' }) {
  let rsp

  rsp = await apiEmss.getUsersForEmsDirect({ id: emsId, ctxt })

  const permissions = rsp.payload.data
  const userIds = permissions.map((x) => parseInt(x.id))
  const users = {}
  permissions.forEach((x) => {
    users[x.id] = new User({
      id: x.id,
      email: x.attributes.email,
      roles: x.attributes.roles
    })
  })
  commit('SET_USERS', users)

  const emsUsers = {}
  for (const userPerm of permissions) {
    const perm = mapPermission(userPerm.attributes.permission)
    for (const role of users[userPerm.id].roles) {
      if (!perm[role]) {
        perm[role] = 0
      }
    }
    emsUsers[userPerm.id] = perm
  }
  commit('SET_USERS_FOR_EMS_DIRECT', { emsId, users: emsUsers })

  rsp = await apiEmss.getGroupsForEms({ id: emsId, ctxt, membership: 'Groups::EmsAccessGroup' })

  const emsGroupIds = rsp.payload.data.map((x) => parseInt(x.id))
  commit('energyManagementSystemGroups/ADD_GROUPS_FOR_EMS', { emsId, emsGroupIds }, { root: true })

  return userIds
}

/**
 * Action to get the All Users related to an EMS through group relations with their permissions per context and the interfering group.
 *
 * @function
 *
 * @params {Integer} emsId, the desired emsId
 *
 * @return {object} the users
 */
export async function getUsersForEmsThroughGroups({ commit, dispatch }, { emsId, ctxt = 'admin' }) {
  const rsp = await apiEmss.getUsersForEmsThroughGroups({ id: emsId, ctxt })

  const permissions = rsp.payload.data

  const userIds = permissions.map((x) => {
    return parseInt(x.id)
  })

  await dispatch('getUsers', { userIds })

  const emsUsers = {}

  for (const userPerm of permissions) {
    const perm = mapPermission(userPerm.attributes.permission)
    perm.emsGroupIds = userPerm.attributes.groupIds
    emsUsers[userPerm.id] = perm
  }

  commit('SET_USERS_FOR_EMS_THROUGH_GROUPS', { emsId, users: emsUsers })

  return userIds
}

/**
 * Action to get the All Users related to an EMS'with a specific permission
 *
 * @function
 *
 * @params {Integer} emsId, the desired emsId
 * @params {object} permissions
 *
 * @return {object} the users
 */
export async function getUsersForEmsWithPermission({ dispatch }, { emsId, ctxt = 'admin', permission = {} }) {
  let userIds = null

  try {
    const rsp = await apiEmss.getUsersForEmsWithPermission({ id: emsId, permission, ctxt })
    userIds = rsp.payload.data.map((x) => parseInt(x.id))
  } catch {
    userIds = []
  }

  return dispatch('getUsers', { userIds, ctxt })
}

/**
 * Action to get all group permissions for an User with their permissions per context and the interfering emsGroups.
 *
 * also sets the emsGroups into the energy_managment_system_groups store
 *
 * @function
 *
 * @params {Integer} userId, the desired userId
 *
 * @return {object} emsGroups relations
 */
export async function getGroupPermissionsForUser({ commit, dispatch }, { userId, ctxt = 'admin' }) {
  const rsp = await apiUsers.getUserRelationshipGroups({ id: userId, ctxt })

  const emsGroupIds = rsp.payload.data.map((x) => {
    return parseInt(x.id)
  })

  await dispatch('energyManagementSystemGroups/getEmsGroups', { emsGroupIds }, { root: true })
  const groupUsers = {}

  for (const groupId of emsGroupIds) {
    const rspEmsPerm = await apiUsers.getUserPermissionGroup({
      id: userId,
      groupId,
      ctxt,
      membership: 'Groups::EmsAccessGroup'
    })
    const perm = mapPermission(rspEmsPerm.payload.data.attributes.permission)

    groupUsers[groupId] = {}
    groupUsers[groupId][userId] = perm
  }
  commit('SET_USERS_FOR_GROUPS', groupUsers)

  return emsGroupIds
}

/**
 * Action to get all directly related EMSs for an User with their permissions per context and the interfering emsGroups.
 *
 * also sets the EMSs into the energy_managment_systems store
 *
 * @function
 *
 * @params {Integer} userId, the desired userId
 *
 * @return {object} direct ems relations
 */
export async function getDirectEmsPermissionsForUser({ commit, dispatch }, { userId, ctxt = 'admin' }) {
  const rsp = await apiUsers.getUserRelationshipEmss({ id: userId, ctxt, membership: 'Groups::EmsAccessGroup' })

  const emsIds = rsp.payload.data.map((x) => {
    return parseInt(x.id)
  })

  await dispatch('energyManagementSystems/getEmss', { emsIds: emsIds }, { root: true })

  const emsUsers = {}

  for (const emsId of emsIds) {
    const rspEmsPerm = await apiUsers.getUserPermissionEms({
      id: userId,
      emsId,
      ctxt,
      membership: 'Groups::EmsAccessGroup'
    })
    const perm = mapPermission(rspEmsPerm.payload.data.attributes.permission)
    emsUsers[emsId] = {}
    emsUsers[emsId][userId] = perm
  }

  commit('SET_USERS_FOR_EMSS_DIRECT', emsUsers)

  return emsIds
}

/**
 * Action to create a Dummy user with default permissions for groups
 *
 * Creates a user with roles customer and admin into the store
 * then creates default permissions for all groups into the store
 *
 * @function
 *
 * @params {Integer} userId, the future userId of the dummy User
 * @params {Integer[]} groupIds, all groups to create permissions for
 *
 */
export function createPermissionDummyForGroups({ commit }, { userId = -1, groupIds }) {
  const dummy = new User({ id: userId, email: 'dummy', roles: ['customer', 'admin'] })
  commit('SET_USER', dummy)

  for (const groupId of groupIds) {
    const permission = new Permission({
      customer: 0,
      admin: 0
    })
    const users = { [userId]: permission }
    commit('SET_USERS_FOR_GROUP', { groupId, users, removeUnmentioned: false })
  }
}

/**
 * Action to Set Community Permissions for
 *
 * Grabs all groupIds in a community and sets the users permission for them based on the given perm tag
 *
 * @function
 *
 * @params {Integer} userId
 * @params {Integer} communityId
 * @params {Object} perms, { 'TRAFO': permTag, 'RESIDENCE': permTag }, with permTag = 'hide'|'show'|'admin'
 *
 */
export function setPermissionsForCommunity({ commit, rootGetters }, { userId, communityId, perms }) {
  const community = rootGetters['communities/getCommunity'](communityId)
  const groupIds = rootGetters['energyManagementSystemGroups/getGroupIdsFromRoot'](community.rootGroupId)
  for (const groupId of groupIds) {
    for (const tag in perms) {
      if (rootGetters['energyManagementSystemGroups/hasTag']({ groupId, tag })) {
        commit('SET_COMMUNITY_PERM', { userId, groupId, perm: perms[tag] })
      }
    }
  }
}

/**
 * Action to update Community Permissions of users to groups based on a reference User
 *
 * reads the reference users permissions and sets them for all user<=>group permutations
 *
 * @function
 *
 * @params {Integer[]} userIds
 * @params {Integer[]} groupIds
 * @params {Integer} referenceUserId
 *
 * @return {Promise}
 */
export async function updateCommunityPermissions(
  { state, commit },
  { ctxt = 'admin', userIds, groupIds, referenceUserId }
) {
  const promises = []
  for (const groupId of groupIds) {
    const perms = state.emsGroups[groupId][referenceUserId]
    for (const userId of userIds) {
      promises.push(
        await apiUsers
          .updateUserGroupPermission({
            ctxt,
            id: userId,
            groupId,
            perms
          })
          .then(() => {
            commit('SET_USERS_FOR_GROUP', { users: { [userId]: perms }, groupId, removeUnmentioned: false })
          })
      )
    }
  }
  return Promise.all(promises)
}

export async function addUsersToGroups({ commit }, { ctxt = 'admin', userIds, groupIds }) {
  const promises = []

  for (const userId of userIds) {
    promises.push(
      apiUsers.addUserToGroups({ ctxt, id: userId, groupIds }).then(() => {
        for (const groupId of groupIds) {
          commit('SET_USERS_FOR_GROUP', {
            groupId,
            users: { [userId]: {} },
            addTrivialPermission: true,
            removeUnmentioned: false
          })
        }
      })
    )
  }

  return Promise.all(promises)
}

export async function removeUsersFromGroups({ commit }, { ctxt = 'admin', userIds, groupIds }) {
  const promises = []

  for (const userId of userIds) {
    promises.push(
      apiUsers.removeUserFromGroups({ ctxt, id: userId, groupIds }).then(() => {
        for (const groupId of groupIds) {
          commit('REMOVE_USER_FROM_GROUP', { groupId, userId })
        }
      })
    )
  }

  return Promise.all(promises)
}

export async function getUserCertifiedPartnerIdentification({ state, commit }, { ctxt = 'admin', userId }) {
  const certifiedPartnerIdentificationId = state.users[userId]?.certifiedPartnerIdentification?.id
  if (!certifiedPartnerIdentificationId) return

  return apiUsers.getCertifiedPartnerIdentification({ ctxt, id: certifiedPartnerIdentificationId }).then((rsp) => {
    const data = rsp.payload.data.attributes
    if (!data) return

    return Promise.all(
      data.energyManagerSerialnos.map((serialno) =>
        apiAmperixes
          .getEnergyManagerExists({ serialno })
          .then(() => {
            data.existingEnergyManagers ||= []
            data.existingEnergyManagers.push(serialno)
          })
          .catch((err) => {
            if (err.code !== 'NOT_FOUND') console.log(err)
          })
      )
    ).then(() => commit('EXTEND_CERTIFIED_PARTNER_IDENTIFICATION_DATA', { userId, data }))
  })
}

export async function fetchCertifiedPartnerGroupMatches({ commit }, { ctxt = 'admin', userId }) {
  commit('energyManagementSystemGroups/RESET_EMS_GROUP_USER_MATCHING_SCORES', {}, { root: true })

  return apiUsers.fetchCertifiedPartnerGroupMatches({ ctxt, userId }).then((rsp) => {
    const matches = rsp.payload.data

    for (const match of matches) {
      const groupId = match.id
      const score = match.attributes?.score || 0

      commit('energyManagementSystemGroups/SET_EMS_GROUP_USER_MATCHING_SCORE', { groupId, score }, { root: true })
    }
  })
}
